/**
 * Build + download a CSV scoped to the Acquisition subtab's KPI cards.
 *
 * The download button is anchored next to the KPI cards in the page
 * toolbar, so admins expect "click → file with these five numbers" — not
 * a multi-section snapshot of every chart on the page (PAMENG-2074 bug:
 * the previous multi-section export was confusing because the button's
 * visual placement implied per-card scope). The Daily Users chart and
 * Acquisition By State map have their own per-section controls; their
 * data is intentionally excluded here.
 *
 * Formula-injection guard: any cell whose leading character is `=`, `+`,
 * `-`, `@`, tab, or carriage return is prefixed with `'` before quoting.
 * Spreadsheet apps interpret those as formula starts, so an attacker who
 * controls a metric label could otherwise smuggle a `=HYPERLINK(...)`
 * into a victim's workbook.
 */

import { Dayjs } from "dayjs";

import type { UserAnalyticsKpisResult } from "../../../queries/user-analytics/userAnalyticsKpisTypes";
import type { FinancialsKpiCardsResponse } from "../../../queries/financials/useGetFinancialsKpiCards";

const DATE_FORMAT = "YYYY-MM-DD";

export type DateRangeTuple = [Dayjs, Dayjs];

export interface AcquisitionCsvInput {
  kpisDates: DateRangeTuple;
  kpis: UserAnalyticsKpisResult | undefined;
  /**
   * `active_deposit_users` is sourced from the financials cards
   * response — moved into Acquisition per the cross-section reorg, so
   * the export needs that response too. Optional so legacy callers
   * don't break and the CSV gracefully omits the row when unavailable.
   */
  financials?: FinancialsKpiCardsResponse | undefined;
}

// Cells that begin with any of these characters are interpreted as formulas
// by Excel / Google Sheets / Numbers — prefix them with a single quote so
// the spreadsheet renders them as plain text.
const FORMULA_TRIGGERS = /^[=+\-@\t\r]/;

export function escapeCsvCell(value: string | number | null | undefined): string {
  // Quote ALL cells — including the empty one for null/undefined — so a
  // diff of the resulting file is structurally stable. (A bare blank cell
  // is also valid CSV, but emitting `""` keeps every column visually
  // aligned in spreadsheet apps and grep output.)
  if (value === null || value === undefined) return `""`;
  const raw = String(value);
  const safe = FORMULA_TRIGGERS.test(raw) ? `'${raw}` : raw;
  return `"${safe.replace(/"/g, '""')}"`;
}

function row(cells: Array<string | number | null | undefined>): string {
  return cells.map(escapeCsvCell).join(",");
}

function fmtRange([start, end]: DateRangeTuple): string {
  return `${start.format(DATE_FORMAT)} to ${end.format(DATE_FORMAT)}`;
}

// Order matches the on-screen card order so the CSV reads the same way
// the page does: Total Users (all-time), then the date-ranged metrics,
// then Total Active Users (Deposit) sourced from the financials response.
const KPI_METRIC_ROWS: Array<{
  key: keyof UserAnalyticsKpisResult;
  label: string;
}> = [
  { key: "total_users", label: "Total Users" },
  { key: "new_users", label: "New Users" },
  { key: "ftd_count", label: "FTD Count" },
  { key: "ftd_amount", label: "FTD Amount" },
  { key: "avg_ftd_amount", label: "Avg FTD Amount" },
];

export function buildAcquisitionCsv(input: AcquisitionCsvInput): string {
  const { kpisDates, kpis, financials } = input;

  const lines: string[] = [];

  lines.push(row(["[Acquisition KPIs]"]));
  lines.push(row([`Time filter: ${fmtRange(kpisDates)}`]));
  lines.push(row([`Period breakdown: Whole range vs equivalent prior period`]));
  lines.push(row(["Metric", "Value", "Previous Value", "Percent Change"]));
  KPI_METRIC_ROWS.forEach(({ key, label }) => {
    const m = kpis?.[key];
    lines.push(
      row([
        label,
        m?.value ?? 0,
        m?.previous_value ?? 0,
        m?.percent_change ?? 0,
      ]),
    );
  });

  const activeDepositUsers = financials?.active_deposit_users;
  lines.push(
    row([
      "Total Active Users (Deposit)",
      activeDepositUsers?.value ?? 0,
      activeDepositUsers?.previous_value ?? 0,
      activeDepositUsers?.percent_change ?? 0,
    ]),
  );

  return lines.join("\n");
}

/** Triggers a browser download for the supplied CSV string. */
export function downloadCsv(filename: string, csv: string): void {
  // Prepend a UTF-8 BOM so Excel correctly detects the encoding and renders
  // non-ASCII state/metric names without mojibake.
  const blob = new Blob(["\uFEFF", csv], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  link.style.display = "none";
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(url);
}

/**
 * Stable, dateless filename following the same convention used by the other
 * KPI subtabs (sportsbook-kpis.xlsx, casino-kpis.xlsx, financials.xlsx),
 * though this tab exports CSV rather than XLSX.
 * The CSV body already records the active date filter so encoding the
 * download date in the filename was redundant — and the previous
 * `acquisition-YYYY-MM-DD.csv` mid-download collisions made re-export
 * comparisons tricky.
 */
export function buildAcquisitionCsvFilename(): string {
  return "kpis-acquisition.csv";
}
